import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_LOST_FOUND_ITEM, UPDATE_LOST_FOUND_ITEM } from '../graphql/lost-found-queries';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import Spinner from '../components/Spinner';
import SafeImage from '../components/SafeImage';
import { API_URL } from '../config';

const CATEGORY_LABELS = {
  LOST_ITEM_FOUND: 'Lost Item Found',
  REPORTED_LOST_ITEM: 'Reported Lost Item',
};

const STATUS_LABELS = {
  PENDING: 'Open',
  FOUND: 'Found',
  RETURNED: 'Returned',
  DISCARDED: 'Discarded',
  SHIPPED: 'Shipped',
};

const STATUS_CLASSES = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  FOUND: 'bg-blue-100 text-blue-800',
  RETURNED: 'bg-green-100 text-green-800',
  DISCARDED: 'bg-red-100 text-red-800',
  SHIPPED: 'bg-emerald-100 text-emerald-800',
};

const SHIPPING_CARRIER_OPTIONS = [
  { value: '', label: 'Select carrier' },
  { value: 'USPS', label: 'USPS' },
  { value: 'UPS', label: 'UPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'DHL', label: 'DHL' },
  { value: 'Amazon', label: 'Amazon' },
  { value: 'Other', label: 'Other' },
];

const LostFoundDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [shippingForm, setShippingForm] = useState({
    shippingAddress: '',
    shippingContactName: '',
    shippingContactEmail: '',
    shippingContactPhone: '',
    shippingCarrier: '',
    trackingNumber: '',
    ccOnFile: false,
    trackingReceiptPhotos: [],
  });
  const [receiptUploading, setReceiptUploading] = useState(false);
  
  const { loading, error, data } = useQuery(GET_LOST_FOUND_ITEM, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const [updateLostFoundItem] = useMutation(UPDATE_LOST_FOUND_ITEM, {
    refetchQueries: ['GetLostFoundItem', 'GetLostFoundItems'],
  });

  useEffect(() => {
    const item = data?.lostFoundItem;
    if (!item) return;

    setShippingForm({
      shippingAddress: item.shippingAddress || '',
      shippingContactName: item.shippingContactName || '',
      shippingContactEmail: item.shippingContactEmail || '',
      shippingContactPhone: item.shippingContactPhone || '',
      shippingCarrier: item.shippingCarrier || '',
      trackingNumber: item.trackingNumber || '',
      ccOnFile: Boolean(item.ccOnFile),
      trackingReceiptPhotos: item.trackingReceiptPhotos || [],
    });
  }, [data?.lostFoundItem]);

  const handleOpenLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
    // Prevent scrolling when lightbox is open
    document.body.style.overflow = 'hidden';
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    // Restore scrolling
    document.body.style.overflow = 'auto';
  };

  const handlePrevPhoto = (e) => {
    e.stopPropagation();
    const item = data?.lostFoundItem;
    if (item?.photos && item.photos.length > 0) {
      setCurrentPhotoIndex((prevIndex) => 
        prevIndex === 0 ? item.photos.length - 1 : prevIndex - 1
      );
    }
  };

  const handleNextPhoto = (e) => {
    e.stopPropagation();
    const item = data?.lostFoundItem;
    if (item?.photos && item.photos.length > 0) {
      setCurrentPhotoIndex((prevIndex) => 
        prevIndex === item.photos.length - 1 ? 0 : prevIndex + 1
      );
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!lightboxOpen) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        handlePrevPhoto(e);
        break;
      case 'ArrowRight':
        handleNextPhoto(e);
        break;
      case 'Escape':
        handleCloseLightbox();
        break;
      default:
        break;
    }
  };

  // Add event listener for keyboard navigation
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Ensure overflow is restored when component unmounts
      document.body.style.overflow = 'auto';
    };
  }, [lightboxOpen, data?.lostFoundItem?.photos]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading lost and found item</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const item = data?.lostFoundItem;
  if (!item) {
    return (
      <div className="bg-yellow-50 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Lost and found item not found</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>The item you are looking for does not exist or has been removed.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleStatusChange = async (newStatus) => {
    try {
      await updateLostFoundItem({
        variables: {
          input: {
            id: item.id,
            status: newStatus
          }
        }
      });
      
      toast.success(`Item marked as ${newStatus.toLowerCase()}`);
      
      // Redirect to the main list after changing to an archived status
      if (newStatus === 'RETURNED' || newStatus === 'DISCARDED' || newStatus === 'SHIPPED') {
        navigate('/dashboard/lost-found');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update item status');
    }
  };

  const handleShippingFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setShippingForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const uploadReceipt = async (file) => {
    if (!file) return;
    setReceiptUploading(true);

    try {
      const response = await fetch(`${API_URL}/api/lost-found/get-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
        }),
      });

      if (!response.ok) throw new Error(`Failed to get upload URL: ${response.statusText}`);

      const { uploadUrl, imageUrl, headers } = await response.json();
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers,
        body: file,
      });

      if (!uploadResponse.ok) throw new Error(`Failed to upload receipt: ${uploadResponse.statusText}`);

      setShippingForm(prev => ({
        ...prev,
        trackingReceiptPhotos: [...prev.trackingReceiptPhotos, imageUrl],
      }));
      toast.success('Tracking receipt uploaded');
    } catch (error) {
      console.error('Error uploading tracking receipt:', error);
      toast.error('Failed to upload tracking receipt');
    } finally {
      setReceiptUploading(false);
    }
  };

  const removeReceiptPhoto = (photoToRemove) => {
    setShippingForm(prev => ({
      ...prev,
      trackingReceiptPhotos: prev.trackingReceiptPhotos.filter(photo => photo !== photoToRemove),
    }));
  };

  const handleSaveShipping = async () => {
    try {
      await updateLostFoundItem({
        variables: {
          input: {
            id: item.id,
            shippingAddress: shippingForm.shippingAddress,
            shippingContactName: shippingForm.shippingContactName,
            shippingContactEmail: shippingForm.shippingContactEmail,
            shippingContactPhone: shippingForm.shippingContactPhone,
            shippingCarrier: shippingForm.shippingCarrier,
            trackingNumber: shippingForm.trackingNumber,
            ccOnFile: shippingForm.ccOnFile,
            trackingReceiptPhotos: shippingForm.trackingReceiptPhotos,
          },
        },
      });

      toast.success(shippingForm.trackingNumber?.trim()
        ? 'Shipping saved and item marked as shipped'
        : 'Shipping details saved');
    } catch (error) {
      console.error('Error saving shipping details:', error);
      toast.error('Failed to save shipping details');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'PPP p');
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Lost Item Details</h1>
        <Link
          to="/dashboard/lost-found"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to List
        </Link>
      </div>

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Lost Item Information</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Details about the lost item.</p>
          </div>
          <div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${STATUS_CLASSES[item.status] || STATUS_CLASSES.PENDING}`}>
              {STATUS_LABELS[item.status] || item.status}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Category</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {CATEGORY_LABELS[item.category] || item.category || 'Lost Item Found'}
                </span>
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Property</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {item.property ? (
                  <div>
                    <div className="font-medium">{item.property.name}</div>
                    <div className="text-gray-500">
                      {item.property.address}
                      {item.property.city && `, ${item.property.city}`}
                      {item.property.state && `, ${item.property.state}`}
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-500">Unknown Property</span>
                )}
              </dd>
            </div>

            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Linked Reservation</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {item.reservation ? (
                  <div>
                    <div className="font-medium">
                      {item.reservation.guest_name || 'Reservation linked'}
                    </div>
                    <div className="text-gray-500">
                      {item.reservation.streamline_reservation_id}
                      {' · '}
                      {formatDate(item.reservation.check_in)}
                      {' to '}
                      {formatDate(item.reservation.check_out)}
                    </div>
                    {item.reservation.email && (
                      <div className="text-gray-500">{item.reservation.email}</div>
                    )}
                    {item.reservation.mobile_phone && (
                      <div className="text-gray-500">{item.reservation.mobile_phone}</div>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-500">No reservation linked</span>
                )}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{item.description}</dd>
            </div>

            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Submitted By</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {item.isGuestSubmitted ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.guestName || 'Guest'}</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        Guest
                      </span>
                    </div>
                    {item.guestEmail && (
                      <span className="text-sm text-gray-500">{item.guestEmail}</span>
                    )}
                  </div>
                ) : item.submitter ? (
                  `${item.submitter.firstName} ${item.submitter.lastName}`
                ) : (
                  <span className="text-gray-500">Unknown</span>
                )}
              </dd>
            </div>

            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Submitted Date</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(item.createdAt)}
              </dd>
            </div>

            {item.foundAt && (
              <>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Found By</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {item.foundBy ? `${item.foundBy.firstName} ${item.foundBy.lastName}` : 'Unknown'}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Found Date</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDate(item.foundAt)}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Return Instructions</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {item.returnOfficeInstructions || 'Return this item to the correct office.'}
                  </dd>
                </div>
              </>
            )}

            {['RETURNED', 'DISCARDED', 'SHIPPED'].includes(item.status) && (
              <>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Resolved By</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {item.resolvedBy ? (
                      `${item.resolvedBy.firstName} ${item.resolvedBy.lastName}`
                    ) : (
                      <span className="text-gray-500">Unknown</span>
                    )}
                  </dd>
                </div>

                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Resolved Date</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDate(item.resolvedAt)}
                  </dd>
                </div>
              </>
            )}

            {(item.shippingAddress || item.shippingContactName || item.shippingContactEmail || item.shippingContactPhone || item.shippingCarrier || item.trackingNumber || item.ccOnFile) && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Shipping</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {item.shippingContactName && <div><strong>Contact:</strong> {item.shippingContactName}</div>}
                  {item.shippingContactEmail && <div><strong>Email:</strong> {item.shippingContactEmail}</div>}
                  {item.shippingContactPhone && <div><strong>Phone:</strong> {item.shippingContactPhone}</div>}
                  {item.shippingAddress && <div className="whitespace-pre-line"><strong>Address:</strong> {item.shippingAddress}</div>}
                  {item.shippingCarrier && <div><strong>Carrier:</strong> {item.shippingCarrier}</div>}
                  {item.trackingNumber && <div><strong>Tracking:</strong> {item.trackingNumber}</div>}
                  {item.ccOnFile && <div>CC on file</div>}
                </dd>
              </div>
            )}

            {item.trackingReceiptPhotos && item.trackingReceiptPhotos.length > 0 && (
              <div className="bg-gray-50 px-4 py-5 sm:px-6">
                <dt className="text-sm font-medium text-gray-500 mb-4">Tracking Receipt</dt>
                <dd className="mt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {item.trackingReceiptPhotos.map((photo, index) => (
                      <SafeImage
                        key={photo}
                        src={photo}
                        alt={`Tracking receipt ${index + 1}`}
                        className="h-48 w-full object-cover rounded-lg"
                        lazy={false}
                      />
                    ))}
                  </div>
                </dd>
              </div>
            )}

            {item.photos && item.photos.length > 0 && (
              <div className="bg-gray-50 px-4 py-5 sm:px-6">
                <dt className="text-sm font-medium text-gray-500 mb-4">Photos</dt>
                <dd className="mt-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {item.photos.map((photo, index) => (
                      <div
                        key={index}
                        className="relative group cursor-pointer"
                        onClick={() => handleOpenLightbox(index)}
                      >
                        <SafeImage
                          src={photo}
                          alt={`Lost item ${index + 1}`}
                          className="h-48 w-full object-cover rounded-lg hover:opacity-90 transition-opacity"
                          lazy={false}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center rounded-lg">
                          <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium transition-opacity">
                            Click to enlarge
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </dd>
              </div>
            )}
          </dl>
        </div>

        {['PENDING', 'FOUND'].includes(item.status) && (
          <div className="bg-gray-50 px-4 py-5 sm:px-6 border-t border-gray-200">
            <h3 className="text-base font-medium text-gray-900 mb-4">Shipping and Tracking</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="shippingContactName" className="block text-sm font-medium text-gray-700">
                  Contact Name
                </label>
                <input
                  id="shippingContactName"
                  name="shippingContactName"
                  type="text"
                  value={shippingForm.shippingContactName}
                  onChange={handleShippingFieldChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="shippingCarrier" className="block text-sm font-medium text-gray-700">
                  Shipping Carrier
                </label>
                <select
                  id="shippingCarrier"
                  name="shippingCarrier"
                  value={shippingForm.shippingCarrier}
                  onChange={handleShippingFieldChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 bg-white shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {SHIPPING_CARRIER_OPTIONS.map((option) => (
                    <option key={option.value || 'none'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="trackingNumber" className="block text-sm font-medium text-gray-700">
                  Tracking Number
                </label>
                <input
                  id="trackingNumber"
                  name="trackingNumber"
                  type="text"
                  value={shippingForm.trackingNumber}
                  onChange={handleShippingFieldChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="shippingContactEmail" className="block text-sm font-medium text-gray-700">
                  Contact Email
                </label>
                <input
                  id="shippingContactEmail"
                  name="shippingContactEmail"
                  type="email"
                  value={shippingForm.shippingContactEmail}
                  onChange={handleShippingFieldChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="shippingContactPhone" className="block text-sm font-medium text-gray-700">
                  Contact Phone
                </label>
                <input
                  id="shippingContactPhone"
                  name="shippingContactPhone"
                  type="tel"
                  value={shippingForm.shippingContactPhone}
                  onChange={handleShippingFieldChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="shippingAddress" className="block text-sm font-medium text-gray-700">
                  Shipping Address
                </label>
                <textarea
                  id="shippingAddress"
                  name="shippingAddress"
                  rows={3}
                  value={shippingForm.shippingAddress}
                  onChange={handleShippingFieldChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  name="ccOnFile"
                  type="checkbox"
                  checked={shippingForm.ccOnFile}
                  onChange={handleShippingFieldChange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                CC on file
              </label>
              <div>
                <label htmlFor="trackingReceipt" className="block text-sm font-medium text-gray-700">
                  Tracking Receipt
                </label>
                <input
                  id="trackingReceipt"
                  type="file"
                  accept="image/*"
                  disabled={receiptUploading}
                  onChange={(e) => uploadReceipt(e.target.files?.[0])}
                  className="mt-1 block w-full text-sm text-gray-700"
                />
              </div>
            </div>

            {shippingForm.trackingReceiptPhotos.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {shippingForm.trackingReceiptPhotos.map((photo) => (
                  <div key={photo} className="relative">
                    <SafeImage
                      src={photo}
                      alt="Tracking receipt"
                      className="h-24 w-full object-cover rounded-lg"
                      lazy={false}
                    />
                    <button
                      type="button"
                      onClick={() => removeReceiptPhoto(photo)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                      aria-label="Remove tracking receipt"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleSaveShipping}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Save Shipping Details
              </button>
            </div>
          </div>
        )}

        {item.status === 'PENDING' && (
          <div className="bg-white px-4 py-5 sm:px-6 border-t border-gray-200">
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => handleStatusChange('FOUND')}
                className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-md text-sm font-medium bg-blue-50 text-blue-800 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Mark as Found
              </button>
              
              <button
                onClick={() => handleStatusChange('DISCARDED')}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium bg-red-50 text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Mark as Discarded
              </button>
            </div>
          </div>
        )}
        {item.status === 'FOUND' && (
          <div className="bg-white px-4 py-5 sm:px-6 border-t border-gray-200">
            <div className="flex justify-end">
              <button
                onClick={() => handleStatusChange('DISCARDED')}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium bg-red-50 text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Mark as Discarded
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && item.photos && item.photos.length > 0 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={handleCloseLightbox}
        >
          <div className="relative max-w-4xl w-full max-h-full">
            {/* Close button */}
            <button 
              className="absolute right-4 top-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all z-10"
              onClick={handleCloseLightbox}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image */}
            <div className="relative">
              <SafeImage 
                src={item.photos[currentPhotoIndex]} 
                alt={`Lost item photo ${currentPhotoIndex + 1}`} 
                className="max-h-[80vh] mx-auto object-contain"
                lazy={false}
              />
              
              {/* Image counter */}
              <div className="absolute bottom-4 left-0 right-0 text-center text-white bg-black bg-opacity-50 py-1">
                {currentPhotoIndex + 1} / {item.photos.length}
              </div>
            </div>
            
            {/* Navigation buttons */}
            {item.photos.length > 1 && (
              <>
                <button 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all"
                  onClick={handlePrevPhoto}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button 
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-all"
                  onClick={handleNextPhoto}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LostFoundDetail; 
